import React, { useCallback, useMemo } from "react";
import { Input, Select, TextArea, FilterPanel } from "@aidkitorg/component-library";
import { SelectOptionType } from "@aidkitorg/component-library";
import { ReportFilterConfig } from "@aidkitorg/types/lib/admin-panel/filters";
import { useLocalizedStrings } from "../../../Localization";
import { useAggregationOptions } from "../hooks/useAggregationOptions";
import { useFieldOptions } from "../../hooks/useFieldOptions";
import { useSettingsFilterFields } from "../../Filters/hooks/useSettingsFilterFields";

// Props type
export type MetricFormFieldsProps = {
  title: string;
  setTitle: (val: string) => void;

  metricType: string;
  setMetricType: (val: string) => void;

  aggregation: string;
  setAggregation: (val: string) => void;

  dataField: string;
  setDataField: (val: string) => void;

  numeratorAggregation: string;
  setNumeratorAggregation: (val: string) => void;

  numerator: string;
  setNumerator: (val: string) => void;

  denominatorAggregation: string;
  setDenominatorAggregation: (val: string) => void;

  denominator: string;
  setDenominator: (val: string) => void;

  decimalPlaces: number;
  setDecimalPlaces: (val: number) => void;

  description?: string;
  setDescription: (val: string) => void;

  filterConfig?: ReportFilterConfig;
  setFilterConfig: (val: ReportFilterConfig | undefined) => void;

  isEditMode?: boolean;
};

export const MetricFormFields: React.FC<MetricFormFieldsProps> = ({
  title,
  setTitle,
  metricType,
  setMetricType,
  aggregation,
  setAggregation,
  dataField,
  setDataField,
  numeratorAggregation,
  setNumeratorAggregation,
  numerator,
  setNumerator,
  denominatorAggregation,
  setDenominatorAggregation,
  denominator,
  setDenominator,
  decimalPlaces,
  setDecimalPlaces,
  description,
  setDescription,
  filterConfig,
  setFilterConfig,
  isEditMode = false,
}) => {
  const L = useLocalizedStrings()
  const { aggregationOptions, percentageAggregationOptions, getAggregationOption, getPercentageAggregationOption } = useAggregationOptions()
  const { fieldOptions, getSelectedFieldOption } = useFieldOptions();
  const availableFieldsForFilters = useSettingsFilterFields();

  const METRIC_TYPE_OPTIONS: SelectOptionType[] = useMemo(() => [
    { value: "numeric", label: L.admin_panel.dashboards.numeric },
    { value: "currency", label: L.admin_panel.dashboards.currency },
    { value: "percentage", label: L.admin_panel.dashboards.percentage },
  ], [])

  const getMetricTypeOption = useCallback((value: string) =>
    METRIC_TYPE_OPTIONS.find((v) => v.value === value), 
  [])

  return (
    <>
      <Input
        label={L.admin_panel.dashboards.metric_title}
        required
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <Select
        required
        portal
        options={METRIC_TYPE_OPTIONS}
        label={L.admin_panel.dashboards.metric_type}
        description={L.admin_panel.dashboards.metric_type_desc}
        labelLocation="above"
        value={getMetricTypeOption(metricType)}
        onChange={(value) => setMetricType(value.value)}
      />

      {/* Aggregation for non-percentage */}
      {metricType !== "percentage" && (
        <Select
          required
          portal
          options={aggregationOptions}
          label={L.admin_panel.dashboards.aggregation}
          description={L.admin_panel.dashboards.aggregation_desc}
          labelLocation="above"
          value={getAggregationOption(aggregation)}
          onChange={(value) => setAggregation(value.value)}
        />
      )}

      {/* Data field for Sum/Mean types (non-percentage) */}
      {metricType !== "percentage" && (
        <Select
          required
          portal
          virtualized
          options={fieldOptions}
          label={L.admin_panel.dashboards.data_field}
          description={L.admin_panel.dashboards.select_field_metric}
          labelLocation="above"
          value={getSelectedFieldOption(dataField)}
          onChange={(value) => {
            if (value) setDataField(value.value)
          }}
          placeholder={L.admin_panel.dashboards.choose_a_field}
          searchable
        />
      )}

      {/* Decimal places for Average aggregation (non-percentage) */}
      {aggregation === "Mean" && metricType !== "percentage" && (
        <Input
          type="number"
          label={L.admin_panel.dashboards.decimal_places}
          min={0}
          max={10}
          value={decimalPlaces.toString()}
          onChange={(e) => {
            const value = parseInt(e.target.value) || 0;
            setDecimalPlaces(Math.max(0, Math.min(10, value)));
          }}
        />
      )}

      {/* Percentage-specific fields */}
      {metricType === "percentage" && (
        <>
          <Select
            required
            portal
            options={percentageAggregationOptions}
            label={L.admin_panel.dashboards.numerator_agg}
            description={L.admin_panel.dashboards.numerator_agg_desc}
            labelLocation="above"
            value={getPercentageAggregationOption(numeratorAggregation)}
            onChange={(value) => setNumeratorAggregation(value.value)}
          />
          {numeratorAggregation !== "Count" && (
            <Select
              required
              portal
              virtualized
              options={fieldOptions}
              label={L.admin_panel.dashboards.numerator}
              description={L.admin_panel.dashboards.numerator_field_desc}
              labelLocation="above"
              value={getSelectedFieldOption(numerator)}
              onChange={(value) => {
                if (value) setNumerator(value.value)
              }}
              placeholder={L.admin_panel.dashboards.choose_a_field}
              searchable
            />
          )}

          <Select
            required
            portal
            options={percentageAggregationOptions}
            label={L.admin_panel.dashboards.denominator_agg}
            description={L.admin_panel.dashboards.denominator_agg_desc}
            labelLocation="above"
            value={getPercentageAggregationOption(denominatorAggregation)}
            onChange={(value) => setDenominatorAggregation(value.value)}
          />
          {denominatorAggregation !== "Count" && (
            <Select
              required
              portal
              virtualized
              options={fieldOptions}
              label={L.admin_panel.dashboards.denominator}
              description={L.admin_panel.dashboards.denominator_field_desc}
              labelLocation="above"
              value={getSelectedFieldOption(denominator)}
              onChange={(value) => {
                if (value) setDenominator(value.value)
              }}
              placeholder={L.admin_panel.dashboards.choose_a_field}
              searchable
            />
          )}
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {L.admin_panel.dashboards.filters_label}
        </label>
        <FilterPanel
          value={filterConfig}
          onChange={setFilterConfig}
          availableFields={availableFieldsForFilters}
          instanceId="metric-form-filters"
          variant="simple"
          inline={isEditMode}
        />
      </div>

      <TextArea
        label={L.admin_panel.dashboards.description}
        showLabel
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </>
  );
};
